import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Send, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api from "@/lib/api";

export const FollowUpDialog = ({ prospect, onClose, onSent }) => {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [drafting, setDrafting] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!prospect) return;
    setSubject(""); setBody(""); setDrafting(true);
    api.post(`/prospects/${prospect.id}/draft-followup`)
      .then((r) => { setSubject(r.data.subject); setBody(r.data.body); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not draft follow-up"))
      .finally(() => setDrafting(false));
  }, [prospect]);

  const copy = async () => {
    await navigator.clipboard.writeText(`Subject: ${subject}\n\n${body}`);
    toast.success("Draft copied to clipboard");
  };

  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`/prospects/${prospect.id}/send-followup`, { subject, body });
      toast.success(data.status === "sent" ? `Follow-up emailed to ${prospect.name}` : `Follow-up recorded (${data.status})`);
      onClose(); onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send follow-up"); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={!!prospect} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="followup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Follow up with {prospect?.name}</DialogTitle>
          <DialogDescription>AI-drafted from your latest traction. Edit freely before sending.</DialogDescription>
        </DialogHeader>
        {drafting ? (
          <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Drafting your follow-up…
          </div>
        ) : (
          <div className="space-y-3">
            <div><Label>Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" data-testid="followup-subject" /></div>
            <div><Label>Message</Label><Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} className="mt-1 text-sm" data-testid="followup-body" /></div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={copy} className="flex-1 gap-2" data-testid="followup-copy"><Copy className="h-4 w-4" /> Copy draft</Button>
              <Button onClick={send} disabled={sending || !prospect?.email || !subject.trim() || !body.trim()} className="flex-1 gap-2" data-testid="followup-send">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send email
              </Button>
            </div>
            {!prospect?.email && <p className="text-xs text-amber-700">This prospect has no email on file. Add one to send directly, or copy the draft.</p>}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
